'use client';

import {
  SvgAdd,
  SvgMyFill,
  SvgShoppingBasketFill,
  SvgStore,
} from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { ServiceSwitchDropdown } from '@/app/_components/ServiceSwitchDropdown';
import { ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { useAdpopcorn } from '@/modules/adpopcorn';

const ANIMATION_DURATION = 300;

type LocalMarketBnbLinkItem = {
  kind: 'link';
  label: string;
  route: { pathname: string };
  icon: typeof SvgStore;
};
type LocalMarketBnbAdpopcornItem = {
  kind: 'adpopcorn';
  label: '혜택';
};
type LocalMarketBnbItem = LocalMarketBnbLinkItem | LocalMarketBnbAdpopcornItem;

const LOCAL_MARKET_BNB_ITEMS: readonly LocalMarketBnbItem[] = [
  {
    kind: 'link',
    label: '홈',
    route: WEL_MARKET_ROUTES.HOME,
    icon: SvgStore,
  },
  {
    kind: 'link',
    label: '장바구니',
    route: WEL_MARKET_ROUTES.CART,
    icon: SvgShoppingBasketFill,
  },
  {
    kind: 'adpopcorn',
    label: '혜택',
  },
  {
    kind: 'link',
    label: '마이페이지',
    route: ROUTES.MY,
    icon: SvgMyFill,
  },
];

interface LocalMarketBnbProps {
  className?: string;
  isMounted?: boolean;
}

export const LocalMarketBnb = ({
  className,
  isMounted = true,
}: LocalMarketBnbProps) => {
  const pathname = usePathname();
  const [menuClickAnimated, setMenuClickAnimated] = useState<{
    [key: number]: boolean;
  }>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const adpopcorn = useAdpopcorn({ placement: 'BOTTOM_NAV_WELMARKET' });

  const visibleItems = LOCAL_MARKET_BNB_ITEMS.filter(
    (item) => item.kind !== 'adpopcorn' || adpopcorn.status !== 'unsupported',
  );

  const handleMenuClick = (index: number) => {
    setMenuClickAnimated((prev) => ({
      ...prev,
      [index]: true,
    }));

    setTimeout(() => {
      setMenuClickAnimated((prev) => ({
        ...prev,
        [index]: false,
      }));
    }, ANIMATION_DURATION);
  };

  return (
    <nav
      aria-label="웰마켙 내비게이션"
      className={cn(
        'relative mx-20 flex items-center rounded-pill bg-Gray-white p-8 shadow-[0px_0px_30px_0px_rgba(0,0,0,0.05),0px_16px_50px_0px_rgba(0,0,0,0.05)]',
        className,
      )}
    >
      <div className="relative z-50">
        <button
          type="button"
          className="relative z-50 size-40 rounded-pill bg-Gray-200 duration-150 flex-center hover:scale-110"
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          aria-label="다른 서비스로 이동"
          aria-expanded={isDropdownOpen}
        >
          <SvgAdd
            className={cn(
              'size-20 text-Gray-600 transition-transform duration-200',
              {
                'rotate-45': isDropdownOpen,
              },
            )}
          />
        </button>
        <ServiceSwitchDropdown
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          currentService="market"
        />
      </div>
      <ul className="flex flex-1 items-center justify-around px-12">
        {visibleItems.map((item, index) => {
          const itemAnimationStyle = {
            animationDuration: `${ANIMATION_DURATION}ms`,
            transitionDelay: `${(index + 1) * 60}ms`,
            transitionProperty: 'transform',
            transitionDuration: '300ms',
          } as const;

          if (item.kind === 'adpopcorn') {
            return (
              <li key={item.label} className="w-53">
                <button
                  type="button"
                  onClick={() => {
                    handleMenuClick(index);
                    adpopcorn.onClick();
                  }}
                  disabled={adpopcorn.isOpening}
                  className={cn(
                    'flex w-full flex-col items-center duration-150 hover:scale-110 disabled:opacity-60',
                    menuClickAnimated[index] && 'animate-jump',
                    isMounted ? 'scale-100' : 'scale-0',
                  )}
                  style={itemAnimationStyle}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="size-28"
                  >
                    <path
                      d="M14 2.33334C20.4327 2.3335 25.665 7.56632 25.665 13.9994C25.665 20.4324 20.4327 25.6652 14 25.6654C12.326 25.6654 10.6389 25.293 9.12402 24.5882C8.82622 24.4459 8.45627 24.4363 8.07715 24.557L5.09863 25.558C4.8773 25.6309 4.652 25.6663 4.43066 25.6664C3.88383 25.6664 3.35225 25.4512 2.9502 25.0492C2.38318 24.4832 2.1882 23.6608 2.43945 22.9046L3.44531 19.9124C3.56258 19.5451 3.55265 19.1759 3.41504 18.8851C2.70667 17.3618 2.33496 15.6746 2.33496 13.9994C2.335 7.56743 7.56839 2.33334 14 2.33334ZM13.999 8.31674C13.4235 8.31674 12.9062 8.63737 12.6514 9.15073L11.6318 11.2005L9.26855 11.5404C8.71217 11.6206 8.25944 12.0026 8.08594 12.5374C7.91267 13.071 8.05683 13.6445 8.45996 14.0345L10.1631 15.682L9.77539 17.9271C9.67744 18.4918 9.90498 19.0543 10.3691 19.3929C10.8345 19.7327 11.4435 19.7777 11.9531 19.5111L13.9971 18.4447L16.042 19.5121C16.2634 19.6269 16.5021 19.6849 16.7402 19.6849C17.0524 19.6848 17.3614 19.5863 17.627 19.3949C18.091 19.0562 18.3196 18.4961 18.2227 17.929L17.834 15.682L19.5371 14.0345C19.939 13.6445 20.0844 13.071 19.9111 12.5374C19.7388 12.0025 19.2849 11.6205 18.7285 11.5404L16.3652 11.2005L15.3457 9.15073C15.0897 8.63631 14.5732 8.31692 13.999 8.31674Z"
                      fill="#D1D5DD"
                    />
                  </svg>
                  <p className="whitespace-nowrap text-center font-500 text-Gray-550 typo-caption2">
                    {item.label}
                  </p>
                </button>
              </li>
            );
          }

          const Icon = item.icon;
          const isActive = (() => {
            if (!pathname) return false;

            if (item.route.pathname === WEL_MARKET_ROUTES.HOME.pathname) {
              return pathname === item.route.pathname;
            }

            return pathname.includes(item.route.pathname);
          })();

          return (
            <li key={item.label} className="w-53">
              <CustomLink
                href={item.route.pathname}
                aria-label={`${item.label} 페이지로 이동`}
                prefetch
                className={cn(
                  'flex w-full flex-col items-center duration-150 hover:scale-110',
                  menuClickAnimated[index] && 'animate-jump',
                  isMounted ? 'scale-100' : 'scale-0',
                )}
                style={itemAnimationStyle}
                onClick={() => handleMenuClick(index)}
              >
                <Icon
                  className={cn('size-28 text-Gray-400', {
                    'text-Gray-black': isActive,
                  })}
                />
                <p
                  className={cn(
                    'whitespace-nowrap text-center font-500 text-Gray-550 typo-caption2',
                    {
                      'font-600 text-Gray-900': isActive,
                    },
                  )}
                >
                  {item.label}
                </p>
              </CustomLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
